import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type { HostPlan, PanelUser } from "../types";

export type HostPlanMode = "hosting" | "vps" | "reseller" | "all";

/**
 * Hosting / VPS / Reseller resource plans.
 * Plan menu → hosting only. Package menu (staff) → VPS & Reseller tags.
 */
export function HostPlansPage({
  plans,
  currentUser,
  onRefresh,
  mode = "hosting"
}: {
  plans: HostPlan[];
  currentUser: PanelUser;
  onRefresh: () => Promise<void>;
  /** hosting = Plan menu; vps/reseller = Package menu tools; all = show filters */
  mode?: HostPlanMode;
}) {
  const isReseller = currentUser.role === "reseller";
  const isStaff =
    currentUser.role === "superadmin" || currentUser.role === "admin";

  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<
    "all" | "standard" | "reseller" | "vps"
  >(() => {
    if (mode === "vps") return "vps";
    if (mode === "reseller") return "reseller";
    if (mode === "hosting") return "standard";
    return "all";
  });

  useEffect(() => {
    if (mode === "vps") setFilter("vps");
    else if (mode === "reseller") setFilter("reseller");
    else if (mode === "hosting") setFilter("standard");
    else setFilter("all");
  }, [mode]);

  const defaultTag = (): "standard" | "reseller" | "vps" => {
    if (isReseller) return "reseller";
    if (mode === "vps") return "vps";
    if (mode === "reseller") return "reseller";
    return "standard";
  };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [planTag, setPlanTag] = useState<"standard" | "reseller" | "vps">(
    defaultTag()
  );
  const [maxRamMb, setMaxRamMb] = useState(1024);
  const [maxCpuCores, setMaxCpuCores] = useState(1);
  const [diskMb, setDiskMb] = useState(10240);
  const [bandwidthMb, setBandwidthMb] = useState(0);
  const [maxDomains, setMaxDomains] = useState(1);
  const [maxDatabases, setMaxDatabases] = useState(2);
  const [maxEmails, setMaxEmails] = useState(5);
  const [maxFtpAccounts, setMaxFtpAccounts] = useState(2);
  const [isDefault, setIsDefault] = useState(false);
  const [active, setActive] = useState(true);

  const canEditPlan = (p: HostPlan) => {
    if (isStaff) return true;
    if (isReseller) return p.ownerUserId === currentUser.id;
    return false;
  };

  /** Plans visible for this mode / role */
  const scopedPlans = useMemo(() => {
    let list = [...plans];
    if (isReseller) {
      // Own plans only for edit list; still see globals as read-only templates
      list = list.filter(
        (p) => p.ownerUserId === currentUser.id || p.ownerUserId == null
      );
    }
    if (mode === "hosting" && isStaff) {
      list = list.filter((p) => p.planTag === "standard" || !p.planTag);
    } else if (mode === "vps") {
      list = list.filter((p) => p.planTag === "vps");
    } else if (mode === "reseller" && isStaff) {
      list = list.filter((p) => p.planTag === "reseller");
    }
    return list;
  }, [plans, mode, isStaff, isReseller, currentUser.id]);

  const filtered = useMemo(() => {
    if (mode !== "all") return scopedPlans;
    if (filter === "all") return scopedPlans;
    return scopedPlans.filter((p) => p.planTag === filter);
  }, [scopedPlans, filter, mode]);

  const pageTitle =
    mode === "vps"
      ? "VPS Plans"
      : mode === "reseller"
        ? "Reseller Plans"
        : "Hosting Plans";

  const pageSub =
    mode === "vps"
      ? "Resource plans for Direct User / VPS-style accounts. Superadmin can edit any plan."
      : mode === "reseller"
        ? "Resource plans used by resellers. Superadmin can view and manage all owners."
        : isReseller
          ? "Create and manage hosting plans for your customer accounts."
          : "Standard hosting plans (RAM, CPU, storage) for accounts. Separate from Owner Packages.";

  const reset = () => {
    setEditId(null);
    setName("");
    setDescription("");
    setPlanTag(defaultTag());
    setMaxRamMb(1024);
    setMaxCpuCores(1);
    setDiskMb(10240);
    setBandwidthMb(0);
    setMaxDomains(1);
    setMaxDatabases(2);
    setMaxEmails(5);
    setMaxFtpAccounts(2);
    setIsDefault(false);
    setActive(true);
  };

  const startEdit = (p: HostPlan) => {
    if (!canEditPlan(p)) {
      setError("You can only edit plans you own.");
      return;
    }
    setEditId(p.id);
    setName(p.name);
    setDescription(p.description);
    setPlanTag(p.planTag);
    setMaxRamMb(p.maxRamMb || 0);
    setMaxCpuCores(p.maxCpuCores || 0);
    setDiskMb(p.diskMb);
    setBandwidthMb(p.bandwidthMb);
    setMaxDomains(p.maxDomains);
    setMaxDatabases(p.maxDatabases);
    setMaxEmails(p.maxEmails);
    setMaxFtpAccounts(p.maxFtpAccounts);
    setIsDefault(p.isDefault);
    setActive(p.active !== false);
    setShow(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    const lockedTag = isReseller
      ? "reseller"
      : mode === "vps"
        ? "vps"
        : mode === "reseller"
          ? "reseller"
          : mode === "hosting"
            ? "standard"
            : planTag;
    const body = {
      name,
      description,
      planTag: lockedTag,
      maxRamMb,
      maxCpuCores,
      diskMb,
      bandwidthMb,
      maxDomains,
      maxSubdomains: maxDomains * 5,
      maxAddonDomains: Math.max(0, maxDomains - 1),
      maxDatabases,
      maxEmails,
      maxFtpAccounts,
      isDefault,
      active
    };
    try {
      if (editId) {
        await api(`/api/v1/plans/${editId}`, {
          method: "PATCH",
          body: JSON.stringify(body)
        });
        setMsg("Plan updated");
      } else {
        await api("/api/v1/plans", {
          method: "POST",
          body: JSON.stringify(body)
        });
        setMsg("Plan created");
      }
      reset();
      setShow(false);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    const p = plans.find((x) => x.id === id);
    if (p && !canEditPlan(p)) {
      setError("You can only delete plans you own.");
      return;
    }
    if (!confirm("Delete this plan?")) return;
    setError("");
    try {
      await api(`/api/v1/plans/${id}`, { method: "DELETE" });
      setMsg("Plan deleted");
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleActive = async (p: HostPlan) => {
    if (!canEditPlan(p)) return;
    setError("");
    try {
      await api(`/api/v1/plans/${p.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: p.name,
          description: p.description,
          planTag: p.planTag,
          maxRamMb: p.maxRamMb,
          maxCpuCores: p.maxCpuCores,
          diskMb: p.diskMb,
          bandwidthMb: p.bandwidthMb,
          maxDomains: p.maxDomains,
          maxSubdomains: p.maxSubdomains,
          maxAddonDomains: p.maxAddonDomains,
          maxDatabases: p.maxDatabases,
          maxEmails: p.maxEmails,
          maxFtpAccounts: p.maxFtpAccounts,
          isDefault: p.isDefault,
          active: !p.active
        })
      });
      setMsg(p.active ? "Plan disabled" : "Plan enabled");
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const tagLabel = (t: string) => {
    if (t === "reseller") return "Reseller Plan";
    if (t === "vps") return "VPS Plan";
    return "Hosting Plan";
  };

  const tagExplain = (t: string) => {
    if (t === "reseller") return "For reseller sub-accounts and seat pools.";
    if (t === "vps") return "Full-server style resources for end users.";
    return "Standard shared hosting plan for customer accounts.";
  };

  const showTypePicker = isStaff && mode === "all";
  const showFilterTabs = isStaff && mode === "all";

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">{pageTitle}</h1>
          <p className="rp-page-sub">{pageSub}</p>
        </div>
        <div className="rp-page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefresh()}
          >
            Refresh
          </button>
          {(isStaff || isReseller) && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() => {
                reset();
                setShow(true);
              }}
            >
              + Add Plan
            </button>
          )}
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {showFilterTabs ? (
        <div className="rp-pkg-tabs">
          {(
            [
              ["all", "All"],
              ["standard", "Hosting Plans"],
              ["vps", "VPS Plans"],
              ["reseller", "Reseller Plans"]
            ] as const
          ).map(([k, lab]) => (
            <button
              key={k}
              type="button"
              className={`rp-pkg-tab${filter === k ? " is-active" : ""}`}
              onClick={() => setFilter(k)}
            >
              {lab}
              {k !== "all"
                ? ` · ${plans.filter((p) => p.planTag === k).length}`
                : ` · ${plans.length}`}
            </button>
          ))}
        </div>
      ) : null}

      <div className="rp-pkg-list">
        {filtered.map((p) => {
          const own = canEditPlan(p);
          return (
            <article key={p.id} className="rp-pkg-row">
              <div>
                <h3 className="rp-pkg-name">
                  {p.name}
                  {p.isDefault ? (
                    <span
                      className="rp-status rp-status-info"
                      style={{ marginLeft: 8, verticalAlign: "middle" }}
                    >
                      Default
                    </span>
                  ) : null}
                  {p.active === false ? (
                    <span
                      className="rp-status rp-status-muted"
                      style={{ marginLeft: 8, verticalAlign: "middle" }}
                    >
                      Disabled
                    </span>
                  ) : (
                    <span
                      className="rp-status rp-status-ok"
                      style={{ marginLeft: 8, verticalAlign: "middle" }}
                    >
                      Active
                    </span>
                  )}
                </h3>
                <p className="rp-pkg-type">
                  {tagLabel(p.planTag)}
                  {p.ownerUserId
                    ? isStaff
                      ? ` · owner ${p.ownerUserId.slice(0, 8)}…`
                      : p.ownerUserId === currentUser.id
                        ? " · yours"
                        : ""
                    : isStaff
                      ? " · global"
                      : " · system"}
                  {p.description ? ` · ${p.description}` : ""}
                </p>
              </div>
              <div className="rp-pkg-price">
                {p.maxRamMb > 0
                  ? p.maxRamMb >= 1024
                    ? `${(p.maxRamMb / 1024).toFixed(0)} GB RAM`
                    : `${p.maxRamMb} MB RAM`
                  : "— RAM"}
                <small>
                  {p.maxCpuCores > 0 ? `${p.maxCpuCores} CPU` : "— CPU"}
                </small>
              </div>
              <div className="rp-pkg-limits">
                <span>
                  <strong>
                    {p.diskMb > 0
                      ? p.diskMb >= 1024
                        ? `${(p.diskMb / 1024).toFixed(0)} GB`
                        : `${p.diskMb} MB`
                      : "∞"}
                  </strong>{" "}
                  storage
                </span>
                <span>
                  <strong>{p.maxDomains}</strong> sites
                </span>
                <span>
                  <strong>{p.maxDatabases}</strong> DBs
                </span>
                <span>
                  <strong>{p.maxEmails}</strong> email
                </span>
              </div>
              <div className="rp-pkg-actions">
                {own ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void toggleActive(p)}
                    >
                      {p.active === false ? "Enable" : "Disable"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => startEdit(p)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ color: "var(--danger)" }}
                      onClick={() => void remove(p.id)}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <span className="faint" style={{ fontSize: 12 }}>
                    Read only
                  </span>
                )}
              </div>
            </article>
          );
        })}
        {!filtered.length ? (
          <div className="rp-empty">
            <strong>No plans yet</strong>
            <p>
              {mode === "vps"
                ? "Create VPS resource plans for direct-user accounts."
                : mode === "reseller"
                  ? "Create reseller resource plans, or wait for resellers to add their own."
                  : "Create Basic / Pro / Business plans for your accounts."}
            </p>
            {(isStaff || isReseller) && (
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto" }}
                onClick={() => {
                  reset();
                  setShow(true);
                }}
              >
                + Add Plan
              </button>
            )}
          </div>
        ) : null}
      </div>

      {show ? (
        <div
          className="rp-drawer-bg"
          onClick={() => {
            if (!busy) {
              setShow(false);
              reset();
            }
          }}
        >
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="plan-editor-title"
          >
            <div className="rp-drawer-hd">
              <div>
                <h2 id="plan-editor-title">
                  {editId ? `Edit ${pageTitle.slice(0, -1)}` : `New ${pageTitle.slice(0, -1)}`}
                </h2>
                <p>
                  {isReseller
                    ? "Reseller hosting resources for your customers."
                    : mode === "vps"
                      ? "VPS resource template."
                      : mode === "reseller"
                        ? "Reseller resource template."
                        : "Hosting resource template for accounts."}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShow(false);
                  reset();
                }}
              >
                ✕
              </button>
            </div>
            <form
              className="rp-drawer-body"
              onSubmit={(e) => void submit(e)}
              id="plan-editor-form"
            >
              <div className="rp-drawer-section">
                <h3>Plan identity</h3>
                <div className="rp-wiz-grid">
                  <div className="field full">
                    <label>Plan name</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Basic / Pro / Business"
                    />
                  </div>
                  <div className="field full">
                    <label>Description</label>
                    <input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Short summary for operators"
                    />
                  </div>
                </div>
                {showTypePicker ? (
                  <div style={{ marginTop: 12 }}>
                    <label
                      style={{
                        fontSize: 11,
                        fontWeight: 650,
                        color: "var(--muted)",
                        display: "block",
                        marginBottom: 8
                      }}
                    >
                      Plan type
                    </label>
                    <div className="rp-tag-row">
                      {(
                        [
                          ["standard", "Hosting Plan"],
                          ["vps", "VPS Plan"],
                          ["reseller", "Reseller Plan"]
                        ] as const
                      ).map(([k, lab]) => (
                        <button
                          key={k}
                          type="button"
                          className={`rp-tag${planTag === k ? " is-selected" : ""}`}
                          onClick={() => setPlanTag(k)}
                        >
                          {lab}
                          <small>{tagExplain(k)}</small>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="faint" style={{ marginTop: 10, fontSize: 13 }}>
                    Type: <strong>{tagLabel(defaultTag())}</strong>
                  </p>
                )}
              </div>

              <div className="rp-drawer-section">
                <h3>Compute</h3>
                <div className="rp-wiz-grid">
                  <div className="field">
                    <label>RAM (MB)</label>
                    <input
                      type="number"
                      min={0}
                      value={maxRamMb}
                      onChange={(e) =>
                        setMaxRamMb(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                  <div className="field">
                    <label>CPU cores</label>
                    <input
                      type="number"
                      min={0}
                      step={0.25}
                      value={maxCpuCores}
                      onChange={(e) =>
                        setMaxCpuCores(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="rp-drawer-section">
                <h3>Storage &amp; network</h3>
                <div className="rp-wiz-grid">
                  <div className="field">
                    <label>Storage (MB)</label>
                    <input
                      type="number"
                      min={0}
                      value={diskMb}
                      onChange={(e) => setDiskMb(Number(e.target.value) || 0)}
                    />
                  </div>
                  <div className="field">
                    <label>Bandwidth (MB, 0 = unlimited)</label>
                    <input
                      type="number"
                      min={0}
                      value={bandwidthMb}
                      onChange={(e) =>
                        setBandwidthMb(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="rp-drawer-section">
                <h3>Websites · Databases · Email · FTP</h3>
                <div className="rp-wiz-grid">
                  <div className="field">
                    <label>Max domains</label>
                    <input
                      type="number"
                      min={0}
                      value={maxDomains}
                      onChange={(e) =>
                        setMaxDomains(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Max databases</label>
                    <input
                      type="number"
                      min={0}
                      value={maxDatabases}
                      onChange={(e) =>
                        setMaxDatabases(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Max emails</label>
                    <input
                      type="number"
                      min={0}
                      value={maxEmails}
                      onChange={(e) =>
                        setMaxEmails(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Max FTP accounts</label>
                    <input
                      type="number"
                      min={0}
                      value={maxFtpAccounts}
                      onChange={(e) =>
                        setMaxFtpAccounts(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="rp-drawer-section">
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />
                  <span>
                    Default plan
                    <small>Used when creating new accounts</small>
                  </span>
                </label>
                <label className="rp-check" style={{ marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  <span>
                    Active
                    <small>Disabled plans cannot be assigned to new accounts</small>
                  </span>
                </label>
              </div>
            </form>
            <div className="rp-drawer-ft">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => {
                  setShow(false);
                  reset();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="plan-editor-form"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy}
              >
                {busy ? "Saving…" : editId ? "Save Plan" : "Create Plan"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
